import { matchRoute } from '@/api/mapbox';
import { coordsEqual, isNullCoords } from '@/utils/MapUtils';
import { FeatureCollection, Feature, LineString, Point, Geometry, lineString } from '@turf/helpers';
import length from '@turf/length';
import lineSliceAlong from '@turf/line-slice-along';
import Vue from 'vue'
import Vuex from 'vuex'
import * as api from '../api/firebase';
import{ FMap } from "../api/firebase"

import AlertStore from "./AlertStore";
import UndoStore from './UndoStore';

Vue.use(Vuex)

export enum MapState {
	None,
	Loading,
	Loaded,
	NotFound,
	Error,
	Saving
}

export default new Vuex.Store({

	modules: {
		alert: AlertStore,
		undo: UndoStore,
	},

	getters: {
		signedIn(state):boolean {
			return state.user != undefined;
		},

		signedInEmail(state):string {
			return state.user? state.user.email : "";
		},

		drawFeature(state):Feature<LineString> {
			let coords = state.map && Array.isArray(state.map.route) ? state.map.route : [];
			if(coords.length == 0)
				coords = [[[0,0]], [[0,0]]]
			return {
				type: "Feature",
				id: "drawFeature",
				geometry: {
					type: "LineString",
					coordinates: coords.map(c => c[0]),
				},
				properties: {}
			}
		},

		trackFeature(state):Feature<LineString> {
			let coords = state.map && Array.isArray(state.map.route) ? state.map.route : [];
			if(coords.length == 0)
				coords = [[[0,0]], [[0,0]]]
			let feature:Feature<LineString> = {
				type: "Feature",
				id: "trackFeature",
				geometry: {
					type: "LineString",
					coordinates: coords.flat(),
				},
				properties: {}
			}
			if(length(feature) > 20) {
				feature = lineSliceAlong(feature, 0, 20);
			}
			return feature;
		},

		heightData(state):number[][] {
			return state.map ? state.map.heights : [];
		},

		trackLength(state, getters) : number {
			return length(getters.trackFeature);
		}

	},

	state: {
		user: undefined as {email:string}|undefined,
		authInitialized: false,
		maps: [] as FMap[],
		map: undefined as FMap|undefined,
		mapState: MapState.None,
		matchRoute: true,
		matchingRoute: false,
		adminData: undefined,
	},

	mutations: {
		setAuthInitialized(state) {
			state.authInitialized = true;
		},
		
		setUser(state, user) {
			state.user = user;
		},

		setMaps(state, maps:FMap[]) {
			state.maps = maps;
		},

		setMap(state, map:FMap) {
			state.map = map;
		},

		setRoute(state, route:number[][][]) {
			if(state.map){
				state.map.route = route;
			}
		},

		setMatchRoute(state, matchRoute:boolean) {
			state.matchRoute = matchRoute;
		},

		setMatchingRoute(state, value:boolean) {
			state.matchingRoute = value;
		},

		// setDrawRoute(state, route:number[][]) {
		// 	if(state.map)
		// 		Vue.set(state.map, "drawRoute", route);
		// },

		setMapState(state, mapState:MapState) {
			state.mapState = mapState;
		},

		setAdminData(state, adminData:any) {
			state.adminData = adminData;
		}
	},
	actions: {
		authStateChanged({commit}, user) {
			console.log("auth state changed", user)
			commit("setUser", user);
			commit("setAuthInitialized");

			if(!user) {
				commit("setMaps", []);
			}
		},

		async loadMaps({commit}) {
			const { data } = await api.loadMaps();
			commit("setMaps", data);
		},

		async loadMap({state, commit, dispatch}, mapId) {
			commit("setMapState", MapState.Loading);
			const { success, data:map, error } = await api.loadMap(mapId);
			if(success) {
				commit("setMap", map);
				commit("setMapState", MapState.Loaded);
			} else {
				commit("setMap", undefined);
				commit("setMapState", error == "not_found" ? MapState.NotFound : MapState.Error);
			}
			dispatch("undo/reset", map ? map.route : [])
		},

		async saveMap({commit, dispatch, state}) {
			if(state.map){
				commit("setMapState", MapState.Saving);
				const result = await api.saveMap(state.map as FMap);
				commit("setMapState", MapState.Loaded);
				if(result.success)
					dispatch("undo/saved");
				return result;
			}
		},

		async deleteMap({commit, state}, id) {
			commit("setMapState", MapState.Saving);
			const result = await api.deleteMap(id);
			commit("setMapState", MapState.Loaded);
			return result;
		},

		async newMap({commit, dispatch, state}) {
			commit("setMap", {
				id: "",
				name: "",
				route: [],
				userId: "",
			});
			dispatch("undo/reset", state.map!.route);
		},

		setRoute({commit, dispatch}, route:number[][][]) {
			commit("setRoute", route);
			dispatch("undo/do", route);
		},

		async patchRoute({commit, dispatch, state, getters}, coords:number[][]) {

			const oldCoords:number[][] = getters.drawFeature.geometry.coordinates;

			if(!state.map)
				return;
			let route = state.map.route.slice();

			const indicesToUpdate = new Set<number>();

			//special case when we only have two coordinates in the same place
			if(coords.length == 2 && coordsEqual(coords[0], coords[1])) {
				route = coords.map(c => [c]);
				indicesToUpdate.add(0);

				//two coords at null island: don't run through routing at all.
				if(isNullCoords(route[0][0]) && isNullCoords(route[1][0])) {
					dispatch("setRoute", route);
					return;
				}
				
			
			// a point has been added
			} else if(coords.length == oldCoords.length + 1) {
				for(let i=0 ; i < coords.length ; ++i) {
					if(!coordsEqual(coords[i], oldCoords[i])) {
						console.log("added point at ", i);
						route.splice(i, 0, [coords[i]]);
						if(i>0) indicesToUpdate.add(i-1);
						if(i<coords.length-1) indicesToUpdate.add(i);
						break;
					}
				}
			
			// point(s) has been moved
			} else if(coords.length == oldCoords.length) {
				for(let i=0 ; i < coords.length ; ++i) {
					if(!coordsEqual(coords[i], oldCoords[i])) {
						route.splice(i, 1, [coords[i]]);
						if(i>0) indicesToUpdate.add(i-1);
						if(i<coords.length-1) indicesToUpdate.add(i);

					}
				}
			
			// point(s) has been deleted
			} else if(coords.length < oldCoords.length) {
				let newIndex = coords.length-1;
				for(let i=oldCoords.length-1 ; i >= 0 ; --i) {
					if(!coordsEqual(oldCoords[i], coords[newIndex])) {
						route.splice(i, 1);
						console.log("deleted point at ", i);
						if(newIndex >= 0) indicesToUpdate.add(newIndex);
					} else {
						--newIndex;
					}
				}
			}

			const toUpdate = Array.from(indicesToUpdate).sort((a,b) => a-b);

			if(state.matchRoute) {
				console.log("update indices", Array.from(indicesToUpdate))
				let curr:number[] = [];
				for(let i=0 ; i<toUpdate.length ; ++i) {
					curr.push(toUpdate[i]);
					if(toUpdate[i]+1 != toUpdate[i+1]) { //end of a contigous path

						if(coords.length > toUpdate[i]+1)
							curr.push(toUpdate[i]+1);

						const currCoords = curr.map(index => coords[index]);
						commit("setMatchingRoute", true);
						let res = await matchRoute(currCoords);
						commit("setMatchingRoute", false);
						for(let j=0 ; j<res.length-1 ; j++) {
							const part = res[j];
							if(j==0 && (curr[0] != 0 || curr.length > 2)) {
								part[0] = currCoords[0];
							}
						}
						if(curr[curr.length-1] != coords.length-1) {
							res = res.slice(0,-1);
						}
						//@ts-ignore
						route.splice(curr[0], res.length, ...res);
						curr = [];
					}
				}
			} else {
				for(const i of toUpdate) {
					route[i] = route[i].slice(0,1);
				}
			}

			dispatch("setRoute", route);
		},

		onUndoRedo({commit}, route) {
			commit("setRoute", route);
		},


		async loadAdminData({commit}) {
			commit("setAdminData", undefined);
			const { data } = await api.loadAdminData();
			commit("setAdminData", data);
		},
	}
})
